import { useOutletContext } from 'react-router-dom';
import { Mail, Phone, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactSection() {
  const { openModal } = useOutletContext();
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState({
    title: 'Get In Touch',
    subtitle: 'Ready to transform your workspace? Contact us today and discover the Circuit difference.',
    phone_numbers: [],
    email_addresses: [],
    office_locations: [],
    form_locations: [],
    workspace_types: []
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    workspace_type: '',
    company_name: '',
    seats_required: '',
    message: '',
  });

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API}/contact-section-content`);
        setContent(response.data);
      } catch (error) {
        console.error('Error fetching contact content:', error);
      }
    };
    fetchContent();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const payload = {
        ...formData,
        seats_required: parseInt(formData.seats_required),
        form_type: 'contact_form',
        page_url: window.location.href,
        utm_source: urlParams.get('utm_source') || null,
        utm_medium: urlParams.get('utm_medium') || null,
        utm_campaign: urlParams.get('utm_campaign') || null,
      };

      await axios.post(`${API}/enquiry`, payload);
      toast.success('Thank you! We will get back to you soon.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        location: '',
        workspace_type: '',
        company_name: '',
        seats_required: '',
        message: '',
      });
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="section-padding bg-white" id="contact" data-testid="contact-section">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl sm:text-5xl font-bold text-[#222] mb-4">{content.title}</h2>
          <p className="text-[#666] text-base max-w-2xl mx-auto">
            {content.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div className="space-y-8">
            <div className="flex items-start space-x-4">
              <div className="w-14 h-14 bg-[#E12429] rounded-lg flex items-center justify-center flex-shrink-0">
                <Phone className="text-white" size={24} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[#222] mb-2">Phone</h3>
                {content.phone_numbers.map((phone, index) => (
                  <p key={index} className="text-[#666] text-base">{phone}</p>
                ))}
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="w-14 h-14 bg-[#E12429] rounded-lg flex items-center justify-center flex-shrink-0">
                <Mail className="text-white" size={24} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[#222] mb-2">Email</h3>
                {content.email_addresses.map((email, index) => (
                  <p key={index} className="text-[#666] text-base">{email}</p>
                ))}
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="w-14 h-14 bg-[#E12429] rounded-lg flex items-center justify-center flex-shrink-0">
                <MapPin className="text-white" size={24} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[#222] mb-2">Locations</h3>
                {content.office_locations.map((location, index) => (
                  <p key={index} className="text-[#666] text-base mb-2">{location}</p>
                ))}
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-[#F5F5F5] p-8 rounded-lg">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="contact-name">Full Name *</Label>
                <Input
                  id="contact-name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="contact-email">Email *</Label>
                  <Input
                    id="contact-email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="contact-phone">Phone *</Label>
                  <Input
                    id="contact-phone"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="contact-location">Location *</Label>
                  <Select value={formData.location} onValueChange={(val) => setFormData({ ...formData, location: val })} required>
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {content.form_locations.map(loc => <SelectItem key={loc} value={loc}>{loc}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="contact-workspace">Workspace Type *</Label>
                  <Select value={formData.workspace_type} onValueChange={(val) => setFormData({ ...formData, workspace_type: val })} required>
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {content.workspace_types.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="contact-company">Company Name</Label>
                  <Input
                    id="contact-company"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="contact-seats">Seats Required *</Label>
                  <Input
                    id="contact-seats"
                    type="number"
                    required
                    value={formData.seats_required}
                    onChange={(e) => setFormData({ ...formData, seats_required: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="contact-message">Message</Label>
                <Textarea
                  id="contact-message"
                  rows={4}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#E12429] hover:bg-[#b81d21]"
                data-testid="contact-submit-button"
              >
                {loading ? 'Sending...' : 'Send Message'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}